import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Card, Modal } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import BGImage from "../../assets/images/SAMPLE1.jpg";
import SubmitGif from "../../assets/images/SubmitGif.gif";
import { forgotPassword, postData } from "../../api/apiService";
import { toast } from "react-toastify";
import { AUTH } from "../../api/endpointMap";

const ResetPassword = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");

    // A token in the URL (reset email or partner invite email) opens the
    // set-password form directly; without one the page is the
    // forgot-password request form.
    if (tokenParam || (redirect === "true" && emailParam)) {
      setIsResetMode(true);
      setEmail(emailParam || "");
      setToken(tokenParam);
    }
  }, [location]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      if (isResetMode) {
        // Call confirm reset password API
        await forgotPassword(AUTH.RESET_PASSWORD, {
          token: token,
          new_password: values.password,
        });

        setLoading(false);
        toast.success("Password reset successfully");
        navigate("/login");
      } else {
        // Call send reset link API
        await postData(AUTH.FORGOT_PASSWORD, {
          email: values.email,
        });

        setTimeout(() => {
          setLoading(false);
          setIsModalVisible(true);
        });
      }
    } catch (err) {
      setLoading(false);
      if (err?.includes("not found")) {
        toast.error("Email Id Not Found");
      } else {
        toast.error(err);
      }
    }
  };

  const handleOk = () => {
    setIsModalVisible(false);
    navigate("/login");
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        flexDirection: "row",
        backgroundColor: "#f0f5ff",
      }}
    >
      <div
        style={{
          flex: 0.6,
          backgroundImage: `url(${BGImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      ></div>

      <div
        style={{
          flex: 0.4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          backgroundColor: "#f0f5ff",
        }}
      >
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            marginBottom: 10,
            zIndex: 2,
            fontSize: window.innerWidth <= 768 ? "20px" : "28px",
            color: window.innerWidth <= 768 ? "#fff" : "#004080",
            fontWeight: 600,
          }}
        >
          {isResetMode ? "Set New Password" : "Reset Password"}
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ width: "100%", maxWidth: "400px" }}
        >
          <Card
            style={{
              padding: "20px",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Form name="reset-password-form" onFinish={handleSubmit}>
              {isResetMode ? (
                <>
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "Please enter new password!" },
                      {
                        min: 8,
                        message: "Password must be at least 8 characters!",
                      },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="New Password"
                    />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password!",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("The two passwords do not match!")
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm Password"
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Email" />
                </Form.Item>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  {isResetMode ? "Set Password" : "Send Reset Link"}
                </Button>
              </Form.Item>
            </Form>
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <a
                onClick={() => navigate("/login")}
                style={{ color: "#004080", cursor: "pointer" }}
              >
                Back to Login
              </a>
            </div>
          </Card>
        </motion.div>
      </div>

      <Modal
        title="Reset Password"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        okText="OK"
      >
        <div className="flex items-center justify-center flex-col">
          <img src={SubmitGif} width={250} height={250} />
          <p className="text-sm text-gray-500 text-center">
            A reset password mail has been sent to your account. Please check
            your inbox.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ResetPassword;
